/**
 * Cities Management Page
 * إدارة المدن
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapPin, Plus, Edit2, Trash2, X, Check,
  AlertTriangle, Info, Search, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#EEF2F7',
  secondary: '#C5A572',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

const REGIONS = [
  'المنطقة الوسطى',
  'المنطقة الغربية',
  'المنطقة الشرقية',
  'منطقة عسير',
  'منطقة مكة المكرمة',
  'منطقة المدينة المنورة',
  'منطقة تبوك',
  'منطقة حائل',
  'منطقة القصيم',
  'منطقة جازان',
  'منطقة نجران',
  'منطقة الباحة',
  'منطقة الجوف',
  'منطقة الحدود الشمالية',
];

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '14px 18px', borderRadius: '10px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={18} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff' },
    secondary: { bg: COLORS.secondary, color: '#fff' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border },
    ghost: { bg: 'transparent', color: COLORS.textMuted },
    danger: { bg: COLORS.error, color: '#fff' },
    success: { bg: COLORS.success, color: '#fff' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
      fontFamily: "'Tajawal', sans-serif", borderRadius: '10px',
      border: v.border ? `1.5px solid ${v.border}` : 'none',
      backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
    }}>
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      outline: 'none', ...style
    }} />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      cursor: 'pointer', outline: 'none', ...style
    }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
    </select>
  );
}

function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px', fontSize: '12px' }}>({hint})</span>}
      </label>
      {children}
      {error && <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error }}>{error}</p>}
    </div>
  );
}

function CityCard({ city, onEdit, onDelete }) {
  return (
    <div style={{
      padding: '16px 20px', borderRadius: '12px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: '48px', height: '48px', borderRadius: '12px',
        backgroundColor: `${COLORS.primary}10`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <MapPin size={24} color={COLORS.primary} />
      </div>
      
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
          {city.name_ar}
        </h3>
        {city.name_en && (
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
            {city.name_en}
          </p>
        )}
        {city.region && (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
            {city.region}
          </p>
        )}
      </div>
      
      {city.services_count !== undefined && (
        <div style={{ textAlign: 'center', padding: '8px 16px', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: COLORS.primary }}>{city.services_count}</div>
          <div style={{ fontSize: '11px', color: COLORS.textMuted }}>خدمة</div>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={() => onEdit(city)} title="تعديل"
          style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: COLORS.primaryLight,
            color: COLORS.primary, cursor: 'pointer' }}>
          <Edit2 size={16} />
        </button>
        <button onClick={() => onDelete(city)} title="حذف"
          style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
            color: COLORS.error, cursor: 'pointer' }}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function CityModal({ isOpen, onClose, onSave, editingItem }) {
  const [formData, setFormData] = useState({
    name_ar: '', name_en: '', region: '', is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setFormData({
          name_ar: editingItem.name_ar || '',
          name_en: editingItem.name_en || '',
          region: editingItem.region || '',
          is_active: editingItem.is_active !== false,
        });
      } else {
        setFormData({ name_ar: '', name_en: '', region: '', is_active: true });
      }
      setError('');
    }
  }, [isOpen, editingItem]);
  
  const validate = () => {
    if (!formData.name_ar.trim()) return 'اسم المدينة بالعربية مطلوب';
    return null;
  };
  
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '500px', maxWidth: '95vw', backgroundColor: COLORS.white, borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
            {editingItem ? 'تعديل المدينة' : 'إضافة مدينة جديدة'}
          </h2>
          <button onClick={onClose} style={{
            width: '36px', height: '36px', borderRadius: '8px', border: 'none',
            backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          <FormField label="اسم المدينة بالعربية" required>
            <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
              placeholder="مثال: الرياض" />
          </FormField>
          
          <FormField label="اسم المدينة بالإنجليزية">
            <Input value={formData.name_en} onChange={e => setFormData({ ...formData, name_en: e.target.value })}
              placeholder="Example: Riyadh" />
          </FormField>
          
          <FormField label="المنطقة">
            <Select value={formData.region}
              onChange={e => setFormData({ ...formData, region: e.target.value })}
              placeholder="-- اختر المنطقة --"
              options={REGIONS} />
          </FormField>
        </div>
        
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: COLORS.bg,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingItem ? 'حفظ التعديلات' : 'إضافة المدينة'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cities`, { headers: getAuthHeaders() });
      setCities(res.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  const handleSave = async (data) => {
    // API only supports create, not update for cities yet
    await axios.post(`${API}/cities`, data, { headers: getAuthHeaders() });
    fetchData();
    setEditingItem(null);
  };
  
  const handleDelete = async (city) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${city.name_ar}"؟`)) return;
    try {
      await axios.delete(`${API}/cities/${city.city_id}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'لا يمكن حذف المدينة - قد تكون مرتبطة بخدمات');
    }
  };
  
  const filtered = cities.filter(c => {
    if (searchQuery && !c.name_ar?.includes(searchQuery) && !c.name_en?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  return (
    <div data-testid="cities-page" style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: `${COLORS.primary}10`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <MapPin size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: COLORS.text }}>المدن</h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة المدن المتاحة للخدمات
            </p>
          </div>
        </div>
      </div>
      
      <Alert type="info" style={{ marginBottom: '24px' }}>
        المدن تُستخدم في تحديد مواقع تقديم الخدمات والفروع. لا يمكن حذف مدينة مرتبطة بخدمات.
      </Alert>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث بالاسم..."
            style={{ paddingRight: '40px' }} />
        </div>
        <Button data-testid="add-city-btn" variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
          إضافة مدينة
        </Button>
      </div>
      
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '12px',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted, gridColumn: '1 / -1' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`, gridColumn: '1 / -1',
          }}>
            <MapPin size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا توجد مدن
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery ? 'لا توجد نتائج تطابق البحث' : 'قم بإضافة مدن جديدة'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
              إضافة أول مدينة
            </Button>
          </div>
        ) : (
          filtered.map(c => (
            <CityCard key={c.city_id} city={c}
              onEdit={(item) => { setEditingItem(item); setShowModal(true); }}
              onDelete={handleDelete} />
          ))
        )}
      </div>
      
      <CityModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        editingItem={editingItem}
      />
    </div>
  );
}
